import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface BoardTheme {
  id: string;
  name: string;
  lightSquare: string;
  darkSquare: string;
  glow?: boolean;
  glowColor?: string;
}

export interface CosmeticsState {
  boardTheme: BoardTheme | null;
  pieceStyle: string;
  avatarAccessory: { type: string; color?: string } | null;
  profileFrame: { type: string; color?: string } | null;
  equippedIds: Record<string, string>;
  loading: boolean;
}

// Default board theme matching Chess.com colors
const defaultBoardTheme: BoardTheme = {
  id: 'default',
  name: 'Classic',
  lightSquare: 'hsl(45, 26%, 87%)',
  darkSquare: 'hsl(159, 55%, 35%)'
};

export function useCosmetics() {
  const { user } = useAuth();
  const [cosmetics, setCosmetics] = useState<CosmeticsState>({
    boardTheme: null,
    pieceStyle: 'modern',
    avatarAccessory: null,
    profileFrame: null,
    equippedIds: {},
    loading: true
  });

  const fetchCosmetics = useCallback(async () => {
    if (!user) {
      setCosmetics(prev => ({ 
        ...prev, 
        loading: false,
        boardTheme: defaultBoardTheme,
        pieceStyle: 'modern',
        equippedIds: {}
      }));
      return;
    }

    try {
      // Fetch user's equipped cosmetics
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('equipped_cosmetics')
        .eq('id', user.id)
        .single();

      const equippedIds = (profile?.equipped_cosmetics as Record<string, string>) || {};

      // If no cosmetics equipped, use defaults
      if (Object.keys(equippedIds).length === 0) {
        setCosmetics({
          boardTheme: defaultBoardTheme,
          pieceStyle: 'modern',
          avatarAccessory: null,
          profileFrame: null,
          equippedIds: {},
          loading: false
        });
        return;
      }

      // Fetch the actual item data for equipped items
      const itemIds = Object.values(equippedIds);
      const { data: items } = await supabase
        .from('shop_items')
        .select('id, category, item_data, name')
        .in('id', itemIds);

      // Process items into usable cosmetics
      let boardTheme: BoardTheme | null = defaultBoardTheme;
      let pieceStyle = 'modern';
      let avatarAccessory: { type: string; color?: string } | null = null;
      let profileFrame: { type: string; color?: string } | null = null;

      items?.forEach(item => {
        const itemData = item.item_data as Record<string, any>;
        
        switch (item.category) {
          case 'board_theme':
            boardTheme = {
              id: item.id,
              name: item.name,
              lightSquare: itemData.lightSquare || defaultBoardTheme.lightSquare,
              darkSquare: itemData.darkSquare || defaultBoardTheme.darkSquare,
              glow: itemData.glow || false,
              glowColor: itemData.glowColor
            };
            break;
          case 'piece_style':
            pieceStyle = itemData.style || 'modern';
            break;
          case 'avatar_accessory':
            avatarAccessory = {
              type: itemData.accessory || itemData.type || 'none',
              color: itemData.color
            };
            break;
          case 'profile_decoration':
            profileFrame = {
              type: itemData.frame || itemData.type || 'none',
              color: itemData.color
            };
            break;
        }
      });

      setCosmetics({
        boardTheme,
        pieceStyle,
        avatarAccessory,
        profileFrame,
        equippedIds,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching cosmetics:', error);
      setCosmetics(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  // Check if a specific item is equipped
  const isEquipped = useCallback((itemId: string): boolean => {
    return Object.values(cosmetics.equippedIds).includes(itemId);
  }, [cosmetics.equippedIds]);

  // Get equipped item ID for a category
  const getEquippedId = useCallback((category: string): string | null => {
    return cosmetics.equippedIds[category] || null;
  }, [cosmetics.equippedIds]);

  // Refresh cosmetics when user changes or items are equipped
  useEffect(() => {
    fetchCosmetics();
  }, [fetchCosmetics]);

  // Listen for cosmetics updates
  useEffect(() => {
    const handleCosmeticsUpdate = () => {
      fetchCosmetics();
    };

    window.addEventListener('cosmetics-updated', handleCosmeticsUpdate);
    return () => window.removeEventListener('cosmetics-updated', handleCosmeticsUpdate);
  }, [fetchCosmetics]);

  return {
    ...cosmetics,
    isEquipped,
    getEquippedId,
    refreshCosmetics: fetchCosmetics,
    defaultBoardTheme
  };
}
